"use client";

import { memo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { cn, POPOVER_CONTENT_CLASSNAME } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Typography } from "./Typography";
import { CalendarRange } from "lucide-react";

type BaseDatePickerProps = {
  ariaLabel?: string;
  emptyLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  inlineExpanded?: boolean;
  onInlineExpandedChange?: (open: boolean) => void;
  className?: string;
  rounded?: "normal" | "full";
  leadingIcon?: React.ReactNode;
  align?: "start" | "center" | "end";
  error?: boolean;
};

export type DatePickerProps =
  | (BaseDatePickerProps & {
      mode: "single";
      initialValue?: Date;
      value?: Date;
      onChange?: (value: Date | undefined) => void;
    })
  | (BaseDatePickerProps & {
      mode: "range";
      initialValue?: DateRange;
      value?: DateRange;
      onChange?: (value: DateRange | undefined) => void;
    });

const DatePicker = (props: DatePickerProps) => {
  const {
    mode,
    ariaLabel = mode === "range" ? "Select date range" : "Select date",
    emptyLabel = mode === "range" ? "Select dates" : "Select date",
    initialValue,
    rounded = "full",
    align = mode === "single" ? "start" : "center",
    leadingIcon,
    className,
    error = false,
  } = props;
  const contentId = `${ariaLabel
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}-popover`;

  const isMobile = useMediaQuery("(max-width: 767px)");
  const isInline = typeof props.onInlineExpandedChange === "function";
  const hasExplicitOpenProp = Object.prototype.hasOwnProperty.call(
    props,
    "open",
  );
  const [internalOpen, setInternalOpen] = useState(false);
  const nonInlineOpen = hasExplicitOpenProp
    ? Boolean(props.open)
    : internalOpen;
  const isOpen = isInline ? Boolean(props.inlineExpanded) : nonInlineOpen;

  const [internalDate, setInternalDate] = useState<Date | undefined>(
    mode === "single" ? (initialValue as Date | undefined) : undefined,
  );
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(
    mode === "range" ? (initialValue as DateRange | undefined) : undefined,
  );

  const hasExplicitValueProp = Object.prototype.hasOwnProperty.call(
    props,
    "value",
  );
  const isSingleControlled = mode === "single" && hasExplicitValueProp;
  const isRangeControlled = mode === "range" && hasExplicitValueProp;

  const selectedDate =
    mode === "single"
      ? isSingleControlled
        ? (props.value as Date | undefined)
        : internalDate
      : undefined;
  const selectedRange =
    mode === "range"
      ? isRangeControlled
        ? (props.value as DateRange | undefined)
        : internalRange
      : undefined;

  const closePopover = () => {
    if (isInline) {
      props.onInlineExpandedChange?.(false);
      return;
    }

    props.onOpenChange?.(false);
    if (!hasExplicitOpenProp) {
      setInternalOpen(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && mode === "range") {
      if (
        selectedRange?.from &&
        (!selectedRange.to ||
          !dayjs(selectedRange.to).isAfter(dayjs(selectedRange.from), "day"))
      ) {
        const autoTo = dayjs(selectedRange.from).add(1, "day").toDate();
        const completedRange = { from: selectedRange.from, to: autoTo };
        props.onChange?.(completedRange);
        if (!isRangeControlled) {
          setInternalRange(completedRange);
        }
      }
    }

    if (isInline) {
      props.onInlineExpandedChange?.(nextOpen);
      return;
    }

    props.onOpenChange?.(nextOpen);
    if (!hasExplicitOpenProp) {
      setInternalOpen(nextOpen);
    }
  };

  const handleSingleChange = (next: Date | undefined) => {
    if (mode !== "single") {
      return;
    }

    props.onChange?.(next);
    if (!isSingleControlled) {
      setInternalDate(next);
    }
    if (isInline && next) {
      props.onInlineExpandedChange?.(false);
    }
  };

  const handleRangeChange = (next: DateRange | undefined) => {
    if (mode !== "range") {
      return;
    }

    props.onChange?.(next);
    if (!isRangeControlled) {
      setInternalRange(next);
    }

    const hasCompleteRange = Boolean(
      next?.from && next?.to && !dayjs(next.from).isSame(next.to, "day"),
    );

    if (!hasCompleteRange) {
      return;
    }

    closePopover();
  };

  const headerLabel = (() => {
    if (mode === "single") {
      if (!selectedDate) return null;

      return {
        date: dayjs(selectedDate).format("DD MMM YYYY"),
        nights: null,
      };
    }

    if (!selectedRange?.from) return null;
    const from = dayjs(selectedRange.from);
    const to = selectedRange.to ? dayjs(selectedRange.to) : null;

    if (!to || from.isSame(to, "day")) {
      return {
        date: from.format("DD MMM YYYY"),
        nights: null,
      };
    }

    const nights = to.diff(from, "day");

    return {
      date: `${from.format("DD MMM")} - ${to.format("DD MMM YYYY")}`,
      nights,
    };
  })();

  const displayValue =
    mode === "single"
      ? !selectedDate
        ? emptyLabel
        : dayjs(selectedDate).format("ddd, DD MMM YYYY")
      : !selectedRange?.from
        ? emptyLabel
        : !selectedRange.to ||
            dayjs(selectedRange.from).isSame(selectedRange.to, "day")
          ? dayjs(selectedRange.from).format("ddd, DD MMM")
          : `${dayjs(selectedRange.from).format("ddd, DD MMM")} - ${dayjs(selectedRange.to).format("ddd, DD MMM")} (${dayjs(selectedRange.to).diff(dayjs(selectedRange.from), "day")} night${dayjs(selectedRange.to).diff(dayjs(selectedRange.from), "day") > 1 ? "s" : ""})`;

  const isSelected =
    mode === "single" ? Boolean(selectedDate) : Boolean(selectedRange?.from);

  const defaultMonth = mode === "single" ? selectedDate : selectedRange?.from;
  const today = dayjs().startOf("day").toDate();

  const trigger = (
    <button
      type="button"
      onClick={() => {
        if (isInline) {
          handleOpenChange(!isOpen);
        }
      }}
      className={cn(
        "flex h-[46px] w-full min-w-0 items-center gap-2 border bg-white text-left hover:border-primary md:h-12 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary",
        rounded === "normal" ? "rounded-xl px-3.5" : "rounded-full px-4",
        isOpen && "border-primary ring-1 ring-primary",
        error && "border-error-foreground focus-visible:ring-error-foreground",
        className,
      )}
      aria-label={ariaLabel}
      aria-controls={contentId}
      aria-expanded={isOpen}
    >
      {leadingIcon ? (
        <span
          className={cn(
            "shrink-0 flex items-center justify-center transition-colors",
            isSelected ? "text-body" : "text-caption",
          )}
        >
          {leadingIcon}
        </span>
      ) : (
        <CalendarRange
          size={20}
          className={cn("shrink-0", isSelected ? "text-body" : "text-caption")}
        />
      )}
      <Typography
        variant="body1"
        className={cn(
          "truncate",
          isSelected ? "text-body font-semibold" : "text-caption font-normal",
        )}
      >
        {displayValue}
      </Typography>
    </button>
  );

  const contentInner = (
    <>
      <div className="flex items-center justify-between gap-2 mb-4">
        <Typography variant="subtitle1">Date Selections</Typography>

        <div className="text-sm text-text-caption text-right">
          {mode === "range" && selectedRange?.from && !selectedRange?.to ? (
            <Typography variant="body2">Select check-out date</Typography>
          ) : headerLabel ? (
            <>
              {headerLabel.date}
              {headerLabel.nights !== null && (
                <>
                  {" "}
                  <span className="font-semibold">
                    ({headerLabel.nights} night
                    {headerLabel.nights > 1 ? "s" : ""})
                  </span>
                </>
              )}
            </>
          ) : (
            <span className="text-[#8A9099]">Please select a date</span>
          )}
        </div>
      </div>

      <div className={mode === "single" ? "flex-1 pb-4" : ""}>
        {mode === "single" ? (
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSingleChange}
            defaultMonth={defaultMonth}
            disabled={{ before: today }}
            showOutsideDays={false}
            className={
              isMobile
                ? "rounded-3xl p-0 [--cell-size:2.65rem]"
                : "rounded-3xl w-full"
            }
            classNames={
              isMobile
                ? {
                    root: "w-full",
                    months: "relative flex flex-col w-full",
                    month: "flex w-full flex-col gap-1.5",
                    weekdays: "mt-1.5 flex w-full",
                    week: "mt-1 flex w-full",
                  }
                : undefined
            }
          />
        ) : (
          <Calendar
            mode="range"
            selected={selectedRange}
            onSelect={handleRangeChange}
            resetOnSelect
            defaultMonth={defaultMonth}
            min={1}
            numberOfMonths={isMobile ? 1 : 2}
            showOutsideDays={false}
            disabled={{ before: today }}
            fixedWeeks={false}
            className={
              isMobile
                ? "rounded-[24px] p-0 [--cell-size:2.65rem]"
                : "rounded-[24px]"
            }
            classNames={
              isMobile
                ? {
                    root: "w-full",
                    months: "relative flex flex-col w-full",
                    month: "flex w-full flex-col gap-1.5",
                    weekdays: "mt-1.5 flex w-full",
                    week: "mt-1 flex w-full",
                  }
                : undefined
            }
          />
        )}
      </div>
    </>
  );

  if (isInline) {
    return (
      <div className="space-y-3">
        {trigger}
        <AnimatePresence initial={false}>
          {isOpen ? (
            <motion.div
              id={contentId}
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
              className="rounded-2xl border p-4 bg-white"
            >
              {contentInner}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <Popover open={nonInlineOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        id={contentId}
        align={align}
        sideOffset={8}
        collisionPadding={16}
        className={cn(POPOVER_CONTENT_CLASSNAME, "md:w-fit p-4")}
      >
        {contentInner}
      </PopoverContent>
    </Popover>
  );
};

export default memo(DatePicker);
